import React, { useState } from "react";
import { Highlight } from "../Highlight";
import { StepHeading } from "../StepHeading";
import { ProductPreview } from "../design/ProductPreview";
import { formatEuro } from "../../utils/pricing";
import type { Configurator } from "../../hooks/useConfigurator";
import type { ContactForm, MockupView } from "../../types/configurator";
interface SendStepProps {
  config: Configurator;
  sending: boolean;
  onSubmit: (form: ContactForm) => void;
}
type Errors = Partial<Record<keyof ContactForm, string>>;
const emptyForm: ContactForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  deadline: "",
  message: ""
};
export function SendStep({
  config,
  sending,
  onSubmit
}: SendStepProps) {
  const {
    product,
    color,
    marking,
    state,
    quote,
    category,
    mockup,
    design
  } = config;
  const [form, setForm] = useState<ContactForm>(emptyForm);
  const [errors, setErrors] = useState<Errors>({});
  if (!product || !quote || !mockup || !color) return null;
  function set<K extends keyof ContactForm>(key: K, value: string) {
    setForm(f => ({
      ...f,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: Errors = {};
    if (!form.name.trim()) next.name = "Indiquez votre nom.";
    if (!form.company.trim()) next.company = "Indiquez votre société.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Adresse e-mail invalide.";
    setErrors(next);
    if (Object.keys(next).length === 0 && !sending) onSubmit(form);
  }
  const els = design.elements;
  const usedZones = mockup.zones.filter(z => els.some(e => e.zoneId === z.id));
  const views = Array.from(new Set(usedZones.map(z => z.view)));
  const previewViews: MockupView[] = views.length ? views : ["front"];
  const counts = [[els.filter(e => e.kind === "image").length, "logo"], [els.filter(e => e.kind === "text").length, "texte"], [els.filter(e => e.kind === "clipart").length, "pictogramme"]] as const;
  const elementsLabel = counts.filter(([n]) => n > 0).map(([n, label]) => `${n} ${label}${n > 1 ? "s" : ""}`).join(", ");
  const recap: [string, string][] = [["Catégorie", category?.name ?? ""], ["Modèle", `${product.name} · ${product.ref}`], ["Couleur", color.name], ["Quantité", `${state.quantity} pièces`], ["Technique", marking?.name ?? "—"], ["Emplacements", usedZones.length ? usedZones.map(z => z.name).join(", ") : "À définir avec nous"], ["Éléments", elementsLabel || "Fichier fourni plus tard"]];
  return <section data-id="element-187" data-mp="38x3yosh80m1r">
      <StepHeading title={<>
            On vous <Highlight data-id="element-189" data-mp="3403s8ggglha4">rappelle</Highlight> ?
          </>} subtitle="Vérifiez votre configuration et laissez vos coordonnées. Un chef de projet vous envoie un devis détaillé sous 24 h ouvrées." data-id="element-188" data-mp="38wua99btqmy6" />

      <div className="grid border border-line lg:grid-cols-[1fr_1.2fr]" data-id="element-190" data-mp="1sj44zz8yxsmy">
        <div className="border-b border-line p-6 md:p-8 lg:border-b-0 lg:border-r" data-id="element-191" data-mp="82mrpjq9iis7">
          <h2 className="text-sm font-bold" data-id="element-192" data-mp="d17jqvvd1w5k">Récapitulatif</h2>
          <div className={`mt-4 grid gap-2 ${previewViews.length > 1 ? "grid-cols-2" : "grid-cols-1 sm:max-w-[280px]"}`} data-id="element-193" data-mp="d1h9cichu1aq">
            {previewViews.map(v => <figure key={v} className="border border-line" data-id="element-194" data-mp="mrahtahca0ar">
                <ProductPreview mockupId={mockup.id} hex={color.hex} view={v} elements={els} markingId={marking?.id ?? null} data-id="element-195" data-mp="rq3z7dzqybg7" />
                <figcaption className="border-t border-line px-3 py-2 text-xs font-bold" data-id="element-196" data-mp="vr5wvqe0mfg2">{v === "front" ? "Face" : "Dos"}</figcaption>
              </figure>)}
          </div>
          <dl className="mt-4" data-id="element-197" data-mp="15pmsx1rt9k8z">
            {recap.map(([k, v]) => <div key={k} className="flex justify-between gap-4 border-b border-ink/15 py-3 text-sm" data-id="element-198" data-mp="15nsgycyip2h1">
                <dt className="text-muted" data-id="element-199" data-mp="32khv0wi0e3vc">{k}</dt>
                <dd className="truncate text-right font-bold" data-id="element-200" data-mp="37iayvivualsg">{v}</dd>
              </div>)}
          </dl>
          <dl className="mt-6 space-y-2 text-sm" data-id="element-201" data-mp="11recsiivhe00">
            <div className="flex justify-between" data-id="element-202" data-mp="wu4qhbqs5ige">
              <dt className="text-muted" data-id="element-203" data-mp="wtcysc0b04ve">Produit / pièce</dt>
              <dd className="tabular-nums" data-id="element-204" data-mp="1atxf91iqxolq">{formatEuro(quote.unitProduct)}</dd>
            </div>
            <div className="flex justify-between" data-id="element-205" data-mp="16riz4p6gp2y5">
              <dt className="text-muted" data-id="element-206" data-mp="16py1gqoup3w2">
                Marquage / pièce · {quote.zoneCount} emplacement{quote.zoneCount > 1 ? "s" : ""}
              </dt>
              <dd className="tabular-nums" data-id="element-207" data-mp="e46bscsh8r3s">{formatEuro(quote.unitMarking)}</dd>
            </div>
            <div className="flex justify-between" data-id="element-208" data-mp="rv1jhlr8p25s">
              <dt className="text-muted" data-id="element-209" data-mp="rww4t16fawae">Frais de calage</dt>
              <dd className="tabular-nums" data-id="element-210" data-mp="n39sja468uf4">{formatEuro(quote.setup)}</dd>
            </div>
            <div className="flex items-baseline justify-between border-t border-ink pt-3" data-id="element-211" data-mp="3vitj21dgjyqh">
              <dt className="font-bold" data-id="element-212" data-mp="3rj2um4belup4">Total estimé HT</dt>
              <dd className="text-2xl font-bold tabular-nums" data-id="element-213" data-mp="3rggng5sfdh7f">{formatEuro(quote.total)}</dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-muted" data-id="element-214" data-mp="3m5l04oxrs1vf">Hors transport. Prix indicatif, confirmé dans votre devis.</p>
        </div>

        <form id="quote-form" noValidate onSubmit={handleSubmit} className="grid content-start gap-5 p-6 sm:grid-cols-2 md:p-8" data-id="element-215" data-mp="37h80nhzh40w6">
          <Field label="Nom et prénom" id="name" required value={form.name} error={errors.name} onChange={v => set("name", v)} autoComplete="name" data-id="element-216" data-mp="3cfjavttokdex" />
          <Field label="Société" id="company" required value={form.company} error={errors.company} onChange={v => set("company", v)} autoComplete="organization" data-id="element-217" data-mp="8z2p0lwgpdb0" />
          <Field label="E-mail professionnel" id="email" type="email" required value={form.email} error={errors.email} onChange={v => set("email", v)} autoComplete="email" data-id="element-218" data-mp="3q90sva6pxi2u" />
          <Field label="Téléphone" id="phone" type="tel" value={form.phone} onChange={v => set("phone", v)} autoComplete="tel" data-id="element-219" data-mp="2eq6gt3ku5h6b" />
          <Field label="Date de livraison souhaitée" id="deadline" type="date" value={form.deadline} onChange={v => set("deadline", v)} data-id="element-220" data-mp="q7vkdtnz9lz5" />
          <div className="sm:col-span-2" data-id="element-221" data-mp="l9tt4lgeegcl">
            <label htmlFor="message" className="text-sm font-bold" data-id="element-222" data-mp="lavcrebhcb5x">
              Message
            </label>
            <textarea id="message" rows={4} value={form.message} onChange={e => set("message", e.target.value)} placeholder="Pantone, contexte de l'opération, adresse de livraison…" className="mt-2 w-full resize-none border border-ink px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-mint" data-id="element-223" data-mp="biz459brp2ng" />
          </div>
          {sending && <p className="text-sm font-bold sm:col-span-2" role="status" data-id="element-224" data-mp="3oqaja5kng2mb">
              Envoi de votre demande…
            </p>}
        </form>
      </div>
    </section>;
}
interface FieldProps {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  autoComplete?: string;
}
function Field({
  label,
  id,
  value,
  onChange,
  type = "text",
  required,
  error,
  autoComplete
}: FieldProps) {
  return <div data-id="element-225" data-mp="2476szawa9l1y">
      <label htmlFor={id} className="text-sm font-bold" data-id="element-226" data-mp="248hwd9ltgqsj">
        {label}
        {required && <span className="text-cherry" data-id="element-227" data-mp="296t6zmjxr5bt"> *</span>}
      </label>
      <input id={id} type={type} value={value} autoComplete={autoComplete} onChange={e => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} className={`mt-2 w-full border px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-mint ${error ? "border-cherry" : "border-ink"}`} data-id="element-228" data-mp="298e2pfhzkub9" />
      {error && <p id={`${id}-error`} className="mt-1 text-xs font-bold text-cherry" data-id="element-229" data-mp="2wwe191ljhp0e">
          {error}
        </p>}
    </div>;
}